import { ACCOUNT_TYPE_LABELS } from '@bcube/shared/constants';
import { Building2, Edit, RefreshCw, User } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { LocalAccount } from '@/context/create-account-flow';
import { maskRg } from '@/lib/formatters';
import { StatusBadge } from './status-badge';

interface AccountCardProps {
  account: LocalAccount;
  billingStatus?: string;
  billingSituation?: string;
  onEdit: () => void;
  onRefresh?: () => void;
  canEdit?: boolean;
}

const formatDocument = (value: string, type: 'cpf' | 'cnpj') => {
  const digits = value.replace(/\D/g, '');
  if (type === 'cpf') {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
};

export function AccountCard({
  account,
  billingStatus,
  billingSituation,
  onEdit,
  onRefresh,
  canEdit = true,
}: AccountCardProps) {
  const isCnpj = account.document?.type === 'cnpj';
  const Icon = isCnpj ? Building2 : User;

  return (
    <TooltipProvider>
      <Card className="h-full">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <Icon className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-lg">Dados da Conta</CardTitle>
              </div>
            </div>

            <div className="flex gap-1">
              {canEdit && onRefresh && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-foreground"
                      onClick={onRefresh}
                      aria-label="Atualizar dados"
                    >
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p>Atualizar dados</p>
                  </TooltipContent>
                </Tooltip>
              )}

              {canEdit && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-foreground"
                      onClick={onEdit}
                      aria-label="Editar dados"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p>Editar dados</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Status badges */}
          {(billingStatus || billingSituation) && (
            <div className="flex flex-wrap gap-2">
              {billingStatus && <StatusBadge status={billingStatus} type="billing" />}
              {billingSituation && <StatusBadge status={billingSituation} type="billing" />}
            </div>
          )}

          {/* Tipo de pessoa */}
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground uppercase">Tipo de pessoa</p>
            <p className="text-sm">{isCnpj ? 'Pessoa Jurídica' : 'Pessoa Física'}</p>
          </div>

          {/* Document */}
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground uppercase">
              {isCnpj ? 'CNPJ' : 'CPF'}
            </p>
            <p className="font-mono text-sm">
              {account.document?.value && account.document?.type
                ? formatDocument(account.document.value, account.document.type)
                : '-'}
            </p>
          </div>

          {isCnpj ? (
            <>
              {/* Razao Social */}
              {account.social_name && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase">
                    Razão Social
                  </p>
                  <p className="text-sm font-medium">{account.social_name}</p>
                </div>
              )}

              {/* Nome Fantasia */}
              {account.fantasy_name && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase">
                    Nome Fantasia
                  </p>
                  <p className="text-sm">{account.fantasy_name}</p>
                </div>
              )}

              {/* Situação CNPJ */}
              {account.status && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase">
                    Situação CNPJ
                  </p>
                  <StatusBadge status={account.status} type="general" />
                </div>
              )}

              {/* IE and IM */}
              <div className="grid grid-cols-2 gap-4">
                {account.state_registration && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground uppercase">
                      Inscrição Estadual
                    </p>
                    <p className="text-sm font-mono">{account.state_registration}</p>
                  </div>
                )}
                {account.municipal_registration && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground uppercase">
                      Inscrição Municipal
                    </p>
                    <p className="text-sm font-mono">{account.municipal_registration}</p>
                  </div>
                )}
              </div>

              {/* Situação IE */}
              {account.situationIE != null && account.situationIE.text != null && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase">Situação IE</p>
                  <StatusBadge status={account.situationIE.text} type="situation" />
                </div>
              )}

              {/* Tipo IE */}
              {account.typeIE && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase">Tipo IE</p>
                  <p className="text-sm">{account.typeIE}</p>
                </div>
              )}

              {/* Porte e Natureza Jurídica */}
              <div className="grid grid-cols-2 gap-4">
                {account.accountType && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground uppercase">Porte</p>
                    <p className="text-sm">
                      {ACCOUNT_TYPE_LABELS[account.accountType] ?? account.accountType}
                    </p>
                  </div>
                )}
                {account.natureText && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground uppercase">
                      Natureza Jurídica
                    </p>
                    <p className="text-sm">{account.natureText}</p>
                  </div>
                )}
              </div>

              {/* Setor */}
              {account.setor && account.setor.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase">Setor</p>
                  <div className="flex flex-wrap gap-1">
                    {account.setor.map((s) => (
                      <Badge key={s} variant="secondary" className="text-xs">
                        {s}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Nome (CPF) */}
              {account.name && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase">Nome</p>
                  <p className="text-sm font-medium">{account.name}</p>
                </div>
              )}

              {/* RG */}
              {account.rg && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase">RG</p>
                  <p className="text-sm font-mono">{maskRg(account.rg)}</p>
                </div>
              )}

              {/* Porte */}
              {account.accountType && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase">Porte</p>
                  <p className="text-sm">
                    {ACCOUNT_TYPE_LABELS[account.accountType] ?? account.accountType}
                  </p>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </TooltipProvider>
  );
}
